import React from 'react'

import moment from 'moment'
import { v4 as uuid } from 'uuid'

// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles'
import Switch from '@material-ui/core/Switch'
import FormControlLabel from '@material-ui/core/FormControlLabel'
// @material-ui/icons
import Timeline from '@material-ui/icons/Timeline'
import Tooltip from '@material-ui/core/Tooltip'
// core components
import GridContainer from '../../components/Grid/GridContainer.js'
import GridItem from '../../components/Grid/GridItem.js'
import Card from '../../components/Card/Card.js'
import CardBody from '../../components/Card/CardBody.js'
import CardIcon from '../../components/Card/CardIcon.js'
import CardHeader from '../../components/Card/CardHeader.js'
import Button from '../../components/CustomButtons/Button.js'
import Icon from '@material-ui/core/Icon'

import styles from '../../assets/jss/material-dashboard-pro-react/views/dashboardStyle.js'
import switchStyles from '../../assets/jss/material-dashboard-pro-react/customCheckboxRadioSwitch.js'

// report components
import SiteSelector from '../Common/SiteSelector.js'
import PeriodSelector from './PeriodSelector.js'
import CustomDates from './CustomDates.js'
import DailySales from './DailySales.js'
import ItemSales from './ItemSales.js'
import SummaryFigures from './SummaryFigures.js'

import { dashboardResponse } from '../../layouts/Aws.js'

const useStyles = makeStyles({
  ...styles,
  ...switchStyles
})

export default function Reports () {
  const periods = {
    last7days: { value: 'last7days', text: 'Last 7 Days', startDate: moment().subtract(1, 'week'), endDate: moment().subtract(1, 'days'), comparisonDate: moment().subtract(2, 'week') },
    last28days: { value: 'last28days', text: 'Last 28 Days', startDate: moment().subtract(28, 'days'), endDate: moment().subtract(1, 'days'), comparisonDate: moment().subtract(56, 'days'), default: true },
    last365days: { value: 'last365days', text: 'Last 365 Days', startDate: moment().subtract(365, 'days'), endDate: moment().subtract(1, 'days'), comparisonDate: moment().subtract(730, 'days') },
    // today: { value: 'today', text: 'Today', startDate: moment(), endDate: moment() },
    thisweek: { value: 'thisweek', text: 'This Week', startDate: moment().startOf('week'), endDate: moment().endOf('week'), comparisonDate: moment().subtract(1, 'week').startOf('week') },
    thismonth: { value: 'thismonth', text: 'This Month', startDate: moment().startOf('month'), endDate: moment().endOf('month'), comparisonDate: moment().startOf('month').subtract(1, 'day').startOf('month') },
    thisquarter: { value: 'thisquarter', text: 'This Quarter', startDate: moment().startOf('quarter'), endDate: moment().endOf('quarter'), comparisonDate: moment().startOf('quarter').subtract(1, 'day').startOf('quarter') },
    thisyear: { value: 'thisyear', text: 'This Year', startDate: moment().startOf('year'), endDate: moment().endOf('year'), comparisonDate: moment().startOf('year').subtract(1, 'day').startOf('year') },
    // yesterday: { value: 'yesterday', text: 'Yesterday', startDate: moment().subtract(1, 'day'), endDate: moment().subtract(1, 'day') },
    lastweek: { value: 'lastweek', text: 'Last Week', startDate: moment().subtract(1, 'week').startOf('week'), endDate: moment().subtract(1, 'week').endOf('week') },
    lastmonth: { value: 'lastmonth', text: 'Last Month', startDate: moment().startOf('month').subtract(1, 'day').startOf('month'), endDate: moment().startOf('month').subtract(1, 'day') },
    lastquarter: { value: 'lastquarter', text: 'Last Quarter', startDate: moment().startOf('quarter').subtract(1, 'day').startOf('quarter'), endDate: moment().startOf('quarter').subtract(1, 'day') },
    lastyear: { value: 'lastyear', text: 'Last Year', startDate: moment().startOf('year').subtract(1, 'day').startOf('year'), endDate: moment().startOf('year').subtract(1, 'day') }
  }

  const [generated, setGenerated] = React.useState(false)

  const [selectedSite, setSelectedSite] = React.useState()
  const [selectedPeriod, setSelectedPeriod] = React.useState()

  const [customStartDate, setCustomStartDate] = React.useState(null)
  const [customEndDate, setCustomEndDate] = React.useState(null)
  const [customComparisonDate, setCustomComparisonDate] = React.useState(null)

  const checkDates = () => {
    if (customStartDate && customEndDate && moment(customStartDate).isAfter(moment(customEndDate))) {
      setCustomEndDate(customStartDate)
    }
    if (customStartDate && customComparisonDate && moment(customStartDate).isBefore(moment(customComparisonDate))) {
      setCustomComparisonDate(customStartDate)
    }
  }

  React.useEffect(checkDates, [customStartDate, customEndDate, customComparisonDate])

  const [useCustomDates, setUseCustomDates] = React.useState(false)

  // this triggers report generation
  const [reportPeriod, setReportPeriod] = React.useState({
    startDate: null,
    endDate: null,
    comparisonDate: null,
    sites: [],
    token: uuid()
  })

  const [siteList, setSiteList] = React.useState()

  const onSiteSelected = (site) => {
    setSelectedSite(site)
  }

  const onPeriodSelected = (period) => {
    setSelectedPeriod(period)
  }

  // this will always be from site or period selection
  const autoGenerateReports = () => {
    // this will also update the custom dates
    if (selectedSite && selectedPeriod) {
      let comparisonDate = null
      if ('comparisonDate' in periods[selectedPeriod]) {
        comparisonDate = periods[selectedPeriod].comparisonDate.format('YYYY-MM-DD')
      }

      const sites = []
      if (selectedSite && selectedSite !== 'all') {
        sites.push(selectedSite)
      }

      setReportPeriod({
        startDate: periods[selectedPeriod].startDate.format('YYYY-MM-DD'),
        endDate: periods[selectedPeriod].endDate.format('YYYY-MM-DD'),
        comparisonDate: comparisonDate,
        sites: sites,
        token: uuid()
      })

      setGenerated(true)
    }
  }

  React.useEffect(autoGenerateReports, [selectedPeriod])

  React.useEffect(() => {
    if (useCustomDates) {
      generateReports()
    } else {
      autoGenerateReports()
    }
  }, [selectedSite])

  // this will always be from custom date range
  const generateReports = () => {
    if (selectedSite) {
      const sites = []
      if (selectedSite && selectedSite !== 'all') {
        sites.push(selectedSite)
      }

      setReportPeriod({
        startDate: customStartDate ? moment(customStartDate).format('YYYY-MM-DD') : null,
        endDate: customEndDate ? moment(customEndDate).format('YYYY-MM-DD') : null,
        comparisonDate: customComparisonDate ? moment(customComparisonDate).format('YYYY-MM-DD') : null,
        sites: sites,
        token: uuid()
      })

      setGenerated(true)
    }
  }

  // initialisation
  React.useEffect(() => {
    dashboardResponse().then(res => {
      setSiteList(res.sites)
    })
  }, [])

  const classes = useStyles()

  const withReports = (p) => {
    return <GridContainer>
      <GridItem xs={12}>
        <SummaryFigures reportPeriod={reportPeriod}/>
      </GridItem>
      <GridItem xs={12} style={{ marginTop: '10px' }}>
        <Card>
          <CardHeader color="danger" icon>
            <CardIcon color="danger">
              <Timeline />
            </CardIcon>
            <h4 className={classes.cardIconTitle} style={{ color: 'grey' }}>
              Daily Sales <small>- Period on Period</small>
            </h4>
          </CardHeader>
          <CardBody>
            <DailySales reportPeriod={reportPeriod}/>
          </CardBody>
        </Card>
      </GridItem>
      <GridItem xs={12}>
        <ItemSales reportPeriod={reportPeriod}/>
      </GridItem>
    </GridContainer>
  }

  const withoutReports = (p) => {
    return null
  }

  return <GridContainer>
    <GridItem xs={12}>
      <Card style={{ marginTop: '0px' }}>
        <CardBody>
          <GridContainer alignItems="center" style={{ height: '100%' }}>
            <GridItem xs={12} sm={4} md={5} lg={5} xl={5}>
              <SiteSelector siteList={siteList} onSelect={onSiteSelected} showAllSitesOption={true} autoSelect={true} />
            </GridItem>
            <GridItem xs={12} sm={4} md={4} lg={5} xl={5}>
              <PeriodSelector periods={periods} onSelect={onPeriodSelected}/>
            </GridItem>
            <GridItem xs={12} sm={4} md={3} lg={2} xl={2} align="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={useCustomDates}
                    onChange={event => setUseCustomDates(event.target.checked)}
                    value="useCustomDates"
                    classes={{
                      switchBase: classes.switchBase,
                      checked: classes.switchChecked,
                      thumb: classes.switchIcon,
                      track: classes.switchBar
                    }}
                  />
                }
                classes={{
                  label: classes.label
                }}
                label="Custom Dates"
              />
            </GridItem>
            { useCustomDates &&
            <GridItem xs={12}>
              <GridContainer>
                <GridItem xs={12} sm={12} md={12} lg={10}>
                  <CustomDates startDate={customStartDate} setStartDate={setCustomStartDate}
                    endDate={customEndDate} setEndDate={setCustomEndDate}
                    comparisonDate={customComparisonDate} setComparisonDate={setCustomComparisonDate} />
                </GridItem>
                <GridItem xs={12} sm={12} md={12} lg={2} align="center">
                  <GridContainer justify="center" alignItems="center" spacing={0}>
                    <GridItem>
                      <Tooltip title="Custom date range limited to maximum 13 months">
                        <div className={classes.stats}>
                          <Icon>info</Icon>
                        </div>
                      </Tooltip>
                    </GridItem>
                    <GridItem>
                      { !selectedSite || (!customStartDate || !customEndDate)
                        ? <Tooltip title="Choose site, start and end date">
                          <div>
                            <Button color="primary" onClick={() => generateReports()} disabled={true}>
                            Generate
                            </Button>
                          </div>
                        </Tooltip>
                        : <Button color="primary" onClick={() => generateReports()} disabled={false}>
                        Generate
                        </Button>
                      }
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            </GridItem> }
          </GridContainer>
        </CardBody>
      </Card>
    </GridItem>
    <GridItem xs={12}>
      { generated ? withReports() : withoutReports() }
    </GridItem>
  </GridContainer>
}
